import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface Quota {
  id: string;
  userId: string;
  periodType: 'MONTHLY' | 'QUARTERLY' | 'ANNUAL';
  periodStart: string;
  periodEnd: string;
  targetAmount: string;
  currency: string;
  notes: string | null;
}

interface AttainmentRow {
  userId: string;
  achievedAmount: string;
  currency: string;
  attainmentPct: string;
}

const PERIOD_TYPES = ['MONTHLY', 'QUARTERLY', 'ANNUAL'] as const;

function pct(row: AttainmentRow) {
  const n = parseFloat(row.attainmentPct);
  if (isNaN(n)) return '—';
  return `${n.toFixed(1)}%`;
}

function fmtMoney(amount: string, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(parseFloat(amount));
  } catch {
    return `${currency} ${amount}`;
  }
}

export function QuotasPage() {
  const [quotas, setQuotas] = useState<Quota[]>([]);
  const [attainment, setAttainment] = useState<AttainmentRow[]>([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    periodType: 'QUARTERLY' as typeof PERIOD_TYPES[number],
    periodStart: '',
    targetAmount: '',
    currency: 'USD',
    notes: '',
  });

  const loadQuotas = useCallback(async () => {
    const { data } = await apiClient.get<Quota[]>('/quotas');
    setQuotas(data);
  }, []);

  const loadAttainment = useCallback(async () => {
    const now = new Date().toISOString().slice(0, 10);
    const { data } = await apiClient.get<AttainmentRow[]>('/quotas/attainment', {
      params: { periodStart: now },
    });
    setAttainment(data);
  }, []);

  useEffect(() => {
    loadQuotas().catch(() => setQuotas([]));
    loadAttainment().catch(() => setAttainment([]));
  }, [loadQuotas, loadAttainment]);

  async function upsertQuota() {
    if (!form.periodStart || !form.targetAmount) return;
    await apiClient.post('/quotas', {
      periodType: form.periodType,
      periodStart: form.periodStart,
      targetAmount: parseFloat(form.targetAmount),
      currency: form.currency,
      notes: form.notes || null,
    });
    setCreating(false);
    setForm({ periodType: 'QUARTERLY', periodStart: '', targetAmount: '', currency: 'USD', notes: '' });
    await loadQuotas();
  }

  async function deleteQuota(id: string) {
    if (!confirm('Eliminar cuota?')) return;
    await apiClient.delete(`/quotas/${id}`);
    await loadQuotas();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Cuotas de ventas</h1>
        <Button size="sm" onClick={() => setCreating((c) => !c)}>
          {creating ? 'Cancelar' : '+ Cuota'}
        </Button>
      </div>

      {creating && (
        <div className="rounded-lg border bg-card p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Nueva cuota
          </h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Período</label>
              <select
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                value={form.periodType}
                onChange={(e) => setForm({ ...form, periodType: e.target.value as typeof PERIOD_TYPES[number] })}
              >
                {PERIOD_TYPES.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Inicio período</label>
              <Input
                type="date"
                value={form.periodStart}
                onChange={(e) => setForm({ ...form, periodStart: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Meta</label>
              <Input
                type="number"
                placeholder="0"
                value={form.targetAmount}
                onChange={(e) => setForm({ ...form, targetAmount: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Moneda</label>
              <Input
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Notas</label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          <Button size="sm" className="mt-4" onClick={upsertQuota}>
            Guardar
          </Button>
        </div>
      )}

      {/* Attainment summary */}
      {attainment.length > 0 && (
        <div className="rounded-lg border bg-card p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Cumplimiento (período actual)
          </h2>
          <div className="space-y-3">
            {attainment.map((a) => {
              const n = parseFloat(a.attainmentPct);
              const barWidth = Math.min(100, isNaN(n) ? 0 : n);
              const color = n >= 100 ? 'bg-emerald-500' : n >= 70 ? 'bg-amber-400' : 'bg-red-500';
              return (
                <div key={a.userId}>
                  <div className="mb-1 flex justify-between text-sm">
                    <span className="font-medium">{a.userId.slice(0, 8)}…</span>
                    <span className="text-muted-foreground">
                      {fmtMoney(a.achievedAmount, a.currency)} · {pct(a)}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-muted">
                    <div
                      className={`h-2 rounded-full ${color}`}
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quota list */}
      <div className="rounded-lg border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-xs uppercase text-muted-foreground">
              <th className="px-4 py-3 text-left">Usuario</th>
              <th className="px-4 py-3 text-left">Período</th>
              <th className="px-4 py-3 text-left">Inicio</th>
              <th className="px-4 py-3 text-right">Meta</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {quotas.map((q) => (
              <tr key={q.id} className="hover:bg-muted/30">
                <td className="px-4 py-2 font-mono text-xs">{q.userId.slice(0, 8)}…</td>
                <td className="px-4 py-2">{q.periodType}</td>
                <td className="px-4 py-2">{q.periodStart}</td>
                <td className="px-4 py-2 text-right">{fmtMoney(q.targetAmount, q.currency)}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    className="text-xs text-muted-foreground hover:text-destructive"
                    onClick={() => deleteQuota(q.id)}
                  >
                    Eliminar
                  </button>
                </td>
              </tr>
            ))}
            {quotas.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                  Sin cuotas registradas
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
